import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PaymentModal = ({ isOpen, onClose, booking, onPaymentComplete }) => {
  const [selectedMethod, setSelectedMethod] = useState('gcash');
  const [gcashNumber, setGcashNumber] = useState('');
  const [gcashName, setGcashName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen || !booking) return null;

  const handlePayment = () => {
    setIsProcessing(true);
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      onPaymentComplete({
        bookingId: booking?.id,
        method: selectedMethod,
        amount: booking?.amount
      });
    }, 2000);
  };

  const paymentMethods = [
    {
      id: 'gcash',
      name: 'GCash',
      icon: 'Smartphone',
      description: 'Instant payment via GCash wallet',
      recommended: true
    },
    {
      id: 'credit_card',
      name: 'Credit/Debit Card',
      icon: 'CreditCard',
      description: 'Visa, Mastercard, JCB accepted',
      recommended: false
    },
    {
      id: 'pay_at_hotel',
      name: 'Pay at Hotel',
      icon: 'Building',
      description: 'Pay when you arrive',
      recommended: false
    }
  ];

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Complete Payment</h2>
            <p className="text-sm text-muted-foreground mt-1">Booking ID: {booking?.id}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Payment Summary */}
          <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-sm mb-1">Amount to Pay</p>
                <h3 className="text-3xl font-bold mb-2">
                  ₱{booking?.amount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </h3>
                <p className="text-sm text-white/90">{booking?.name}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Icon name="Wallet" size={24} />
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Select Payment Method
            </label>
            <div className="space-y-3">
              {paymentMethods?.map((method) => (
                <button
                  key={method?.id}
                  onClick={() => setSelectedMethod(method?.id)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    selectedMethod === method?.id
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      selectedMethod === method?.id ? 'bg-primary/20' : 'bg-muted'
                    }`}>
                      <Icon name={method?.icon} size={24} className={selectedMethod === method?.id ? 'text-primary' : 'text-foreground'} />
                    </div>
                    <div className="text-left">
                      <h4 className="font-semibold text-foreground">{method?.name}</h4>
                      <p className="text-sm text-muted-foreground">{method?.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {method?.recommended && (
                      <span className="px-3 py-1 rounded-full bg-success text-success-foreground text-xs font-semibold">
                        Recommended
                      </span>
                    )}
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedMethod === method?.id
                        ? 'border-primary bg-primary' :'border-muted-foreground'
                    }`}>
                      {selectedMethod === method?.id && (
                        <div className="w-2 h-2 rounded-full bg-white" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Payment Form - GCash */}
          {selectedMethod === 'gcash' && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-xl border border-border">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Smartphone" size={20} className="text-primary" />
                <h4 className="font-semibold text-foreground">GCash Payment Details</h4>
              </div>
              <Input
                type="tel"
                label="GCash Mobile Number"
                placeholder="09XX XXX XXXX"
                value={gcashNumber}
                onChange={(e) => setGcashNumber(e?.target?.value)}
                required
              />
              <Input
                type="text"
                label="Account Name"
                placeholder="Juan Dela Cruz"
                value={gcashName}
                onChange={(e) => setGcashName(e?.target?.value)}
                required
              />
              <div className="flex items-start gap-3 p-3 bg-primary/10 rounded-lg border border-primary/20">
                <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs text-foreground">
                  You will receive a GCash payment request notification. Please approve the transaction within 5 minutes.
                </p>
              </div>
            </div>
          )}

          {/* Payment Form - Credit Card */}
          {selectedMethod === 'credit_card' && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-xl border border-border">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="CreditCard" size={20} className="text-primary" />
                <h4 className="font-semibold text-foreground">Card Payment Details</h4>
              </div>
              <Input
                type="text"
                label="Card Number"
                placeholder="1234 5678 9012 3456"
                value={cardNumber}
                onChange={(e) => setCardNumber(e?.target?.value)}
                required
              />
              <Input
                type="text"
                label="Cardholder Name"
                placeholder="Juan Dela Cruz"
                value={cardName}
                onChange={(e) => setCardName(e?.target?.value)}
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="text"
                  label="Expiry Date"
                  placeholder="MM/YY"
                  value={cardExpiry}
                  onChange={(e) => setCardExpiry(e?.target?.value)}
                  required
                />
                <Input
                  type="text"
                  label="CVV"
                  placeholder="123"
                  value={cardCvv}
                  onChange={(e) => setCardCvv(e?.target?.value)}
                  required
                />
              </div>
              <div className="flex items-start gap-3 p-3 bg-success/10 rounded-lg border border-success/20">
                <Icon name="Shield" size={16} className="text-success mt-0.5 flex-shrink-0" />
                <p className="text-xs text-foreground">
                  Your payment is secured with 256-bit SSL encryption. Card details are never stored.
                </p>
              </div>
            </div>
          )}

          {/* Payment Form - Pay at Hotel */}
          {selectedMethod === 'pay_at_hotel' && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-xl border border-border">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="Building" size={20} className="text-primary" />
                <h4 className="font-semibold text-foreground">Pay at Hotel</h4>
              </div>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-foreground">
                    <p className="font-medium mb-1">Payment Instructions:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Present your booking confirmation at check-in</li>
                      <li>Payment can be made via cash, card, or GCash at the property</li>
                      <li>Full payment is required before check-in</li>
                      <li>Cancellation policy still applies</li>
                    </ul>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-warning/10 rounded-lg border border-warning/20">
                  <Icon name="AlertCircle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-foreground">
                    Please note: Some properties may require a deposit or credit card guarantee at check-in.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Security Notice */}
          <div className="flex items-start gap-3 p-4 bg-card border border-border rounded-xl">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={20} className="text-success" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1">Secure Payment</h4>
              <p className="text-xs text-muted-foreground">
                All transactions are encrypted and secured with industry-standard SSL technology. Your payment information is protected.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              size="lg"
              fullWidth
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              size="lg"
              fullWidth
              onClick={handlePayment}
              loading={isProcessing}
              iconName={selectedMethod === 'pay_at_hotel' ? 'Check' : 'CreditCard'}
              iconPosition="left"
            >
              {isProcessing
                ? 'Processing...'
                : selectedMethod === 'pay_at_hotel' ?'Confirm Booking'
                : `Pay ₱${booking?.amount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;